/**
 * METIELLE – servizi aggiuntivi
 *
 * 1. Pagina prodotto: casella "Personalizza con le tue iniziali" (+20 €) con campo da 2 lettere.
 *    Le lettere vengono salvate sul capo come proprietà "Iniziali" e, dopo l'aggiunta al carrello,
 *    viene aggiunto il prodotto "Iniziali personalizzate" collegato al capo.
 * 2. Carrello (drawer e pagina): promemoria per aggiungere le iniziali ai capi che non le hanno
 *    e scelta dell'imballaggio (busta riciclata gratuita / scatola in cartone a pagamento).
 * 3. Pulizia automatica: rimuove iniziali o scatola rimaste nel carrello senza il capo collegato.
 *
 * La configurazione (ID varianti e prezzi) arriva da snippets/metielle-addons.liquid.
 */
// URL completo (lo stesso dell'import map del tema): lo script viene caricato prima dell'import map.
import { CartLinesUpdateEvent, StandardEvents } from 'https://cdn.shopify.com/storefront/standard-events.js';

const configElement = document.getElementById('metielle-addons-config');
/** @type {Record<string, any>} */
let cfg = {};
try {
  cfg = JSON.parse(configElement?.textContent || '{}');
} catch (error) {
  console.warn('[metielle-addons] Configurazione non valida', error);
}

const INITIALS_HANDLE = 'iniziali-personalizzate';
const BOX_HANDLE = 'scatola-in-cartone';
const ADDON_HANDLES = [INITIALS_HANDLE, BOX_HANDLE];
const INITIALS_PROPERTY = 'Iniziali';
const GARMENT_PROPERTY = 'Capo';
const CONSENT_PROPERTY = 'Condizioni personalizzazione';
const CONSENT_VALUE = 'Accettate: iniziali permanenti, capo escluso dal recesso';
const POLICY_URL = '/policies/refund-policy';

const TEXT = {
  productToggle: 'Personalizza con le tue iniziali',
  productFieldLabel: 'Le tue iniziali (2 lettere)',
  productNote: 'Applicate a mano nel nostro atelier prima della spedizione.',
  consentBefore: 'Ho letto e accetto le',
  consentLink: 'condizioni di personalizzazione',
  consentAfter: ': le iniziali sono permanenti e non rimovibili, e il capo personalizzato è escluso dal diritto di recesso.',
  consentRequired: 'Per continuare accetta le condizioni di personalizzazione.',
  inGift: 'In omaggio',
  invalidLetters: 'Inserisci 2 lettere (A-Z).',
  cartInitialsTitle: 'Aggiungi le tue iniziali',
  cartInitialsText: 'Le applichiamo a mano sul capo prima della spedizione, in modo permanente.',
  cartInitialsGarment: 'Capo da personalizzare',
  cartInitialsButton: 'Aggiungi',
  packagingTitle: 'Come vuoi ricevere il tuo ordine?',
  packagingBag: 'Busta impermeabile in plastica riciclata',
  packagingBox: 'Scatola in cartone',
  free: 'Gratis',
  genericError: 'Non è stato possibile aggiornare il carrello. Riprova.',
};

const routes = /** @type {any} */ (window).Theme?.routes || {};
/** @param {string | undefined} url @param {string} fallback */
const withJs = (url, fallback) => {
  const base = url || fallback;
  return base.endsWith('.js') ? base : `${base}.js`;
};
const CART_ADD_URL = withJs(routes.cart_add_url, '/cart/add');
const CART_CHANGE_URL = withJs(routes.cart_change_url, '/cart/change');
const CART_UPDATE_URL = withJs(routes.cart_update_url, '/cart/update');
const CART_URL = withJs(routes.cart_url, '/cart');

/** @type {any | null} */
let lastCart = null;
let busy = false;
let instanceCounter = 0;

/** Stato del campo iniziali sulla pagina prodotto, ripristinato se il tema ridisegna la sezione. */
const productState = { checked: false, letters: '', consent: false };

/* -------------------------------------------------------------------------- */
/* Utilità                                                                     */
/* -------------------------------------------------------------------------- */

/** @param {string} value */
const sanitizeLetters = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase()
    .slice(0, 2);

/** @param {string} value */
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const initialsPriceLabel = () => (cfg.initialsFree ? TEXT.inGift : `+${cfg.initialsPrice || ''}`);

/** @param {string} attributes */
const consentMarkup = (attributes) => `
  <label class="mt-consent">
    <input type="checkbox" class="mt-consent__checkbox" ${attributes}>
    <span class="mt-consent__text">${escapeHtml(TEXT.consentBefore)} <a href="${POLICY_URL}" target="_blank" rel="noopener">${escapeHtml(
      TEXT.consentLink
    )}</a>${escapeHtml(TEXT.consentAfter)}</span>
  </label>
`;

/** @param {any} item */
const isAddon = (item) => ADDON_HANDLES.includes(item.handle);

/** Sezioni del carrello presenti nella pagina, da ridisegnare dopo ogni modifica. */
const cartSectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);

/**
 * @param {string} url
 * @param {Record<string, any>} body
 */
async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status || data.errors) {
    throw new Error(data.description || data.message || data.errors || `Errore ${response.status}`);
  }
  return data;
}

async function fetchCart() {
  const response = await fetch(CART_URL, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Carrello non disponibile: ${response.status}`);
  lastCart = await response.json();
  return lastCart;
}

/**
 * Dopo una modifica fatta da questo script: aggiorna drawer, pagina carrello e icona.
 * @param {Record<string, string> | undefined} sections
 */
async function refreshCartUI(sections) {
  const cart = await fetchCart();
  let dispatched = false;

  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: cart.items.map((/** @type {any} */ item) => ({ id: item.key, quantity: item.quantity })),
      promise: deferred.promise,
    });
    /** @type {any} */ (event).__metielle = true;
    document.dispatchEvent(event);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'metielle-addons',
        didError: false,
      },
    });
    dispatched = true;
  } catch (error) {
    console.warn('[metielle-addons] Evento carrello non disponibile, ridisegno le sezioni', error);
  }

  if (!dispatched || !sections) {
    try {
      const { sectionRenderer } = await import(cfg.sectionRendererUrl || '@theme/section-renderer');
      for (const element of document.querySelectorAll('cart-items-component')) {
        const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
        if (!id) continue;
        await sectionRenderer.renderSection(id, {
          cache: false,
          mode: /** @type {HTMLElement} */ (element).dataset.drawer !== undefined ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      console.warn('[metielle-addons] Ridisegno sezioni non riuscito', error);
    }
  }

  // Il ridisegno del tema rimuove i blocchi iniettati: li ricostruiamo con i dati aggiornati.
  window.setTimeout(() => renderCartBlocks(true), 60);
  return cart;
}

/* -------------------------------------------------------------------------- */
/* Pagina prodotto                                                             */
/* -------------------------------------------------------------------------- */

function productInitialsEnabled() {
  return Boolean(cfg.initialsVariantId) && cfg.productId && !cfg.productIsAddon && !cfg.productIsGiftCard;
}

/** @param {string} uid */
function buildProductInitials(uid) {
  const wrapper = document.createElement('div');
  wrapper.className = 'mt-initials';
  wrapper.dataset.mtInitials = '';
  wrapper.innerHTML = `
    <label class="mt-initials__toggle">
      <input type="checkbox" class="mt-initials__checkbox" data-mt-initials-toggle>
      <span class="mt-initials__label">${escapeHtml(TEXT.productToggle)}</span>
      <span class="mt-initials__price">${escapeHtml(initialsPriceLabel())}</span>
    </label>
    <div class="mt-initials__field" data-mt-initials-field hidden>
      <label class="mt-initials__field-label" for="${uid}">${escapeHtml(TEXT.productFieldLabel)}</label>
      <input
        id="${uid}"
        class="mt-initials__input"
        type="text"
        name="properties[${INITIALS_PROPERTY}]"
        maxlength="2"
        minlength="2"
        pattern="[A-Za-z]{2}"
        autocomplete="off"
        autocapitalize="characters"
        spellcheck="false"
        placeholder="MM"
        required
        disabled
        data-mt-initials-input
      >
      <small class="mt-initials__note">${escapeHtml(TEXT.productNote)}</small>
      ${consentMarkup(
        `name="properties[${CONSENT_PROPERTY}]" value="${escapeHtml(CONSENT_VALUE)}" required disabled data-mt-initials-consent`
      )}
    </div>
  `;

  const toggle = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-mt-initials-toggle]'));
  const input = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-mt-initials-input]'));
  const consent = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-mt-initials-consent]'));
  toggle.checked = productState.checked;
  input.value = productState.letters;
  consent.checked = productState.consent;
  applyToggleState(wrapper);
  return wrapper;
}

/** @param {Element} wrapper */
function applyToggleState(wrapper) {
  const toggle = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-toggle]'));
  const field = /** @type {HTMLElement | null} */ (wrapper.querySelector('[data-mt-initials-field]'));
  const input = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-input]'));
  const consent = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-consent]'));
  if (!toggle || !field || !input) return;
  field.hidden = !toggle.checked;
  input.disabled = !toggle.checked;
  validateProductInput(input);
  if (consent) {
    consent.disabled = !toggle.checked;
    validateConsent(consent);
  }
}

/** @param {HTMLInputElement} consent */
function validateConsent(consent) {
  consent.setCustomValidity(consent.disabled || consent.checked ? '' : TEXT.consentRequired);
}

/** @param {HTMLInputElement} input */
function validateProductInput(input) {
  const valid = input.disabled || /^[A-Z]{2}$/.test(input.value);
  input.setCustomValidity(valid ? '' : TEXT.invalidLetters);
}

function injectProductInitials() {
  if (!productInitialsEnabled()) return;

  const forms = document.querySelectorAll('product-form-component form[data-type="add-to-cart-form"]');
  for (const form of forms) {
    const productForm = /** @type {HTMLElement | null} */ (form.closest('product-form-component'));
    if (!productForm || String(productForm.dataset.productId) !== String(cfg.productId)) continue;
    if (form.closest('product-card, .quick-add-modal, dialog')) continue;
    if (form.querySelector('[data-mt-initials]')) continue;

    const uid = `mt-initials-${++instanceCounter}`;
    const block = buildProductInitials(uid);
    const buttons = form.querySelector('.product-form-buttons');
    if (buttons) {
      buttons.before(block);
    } else {
      form.prepend(block);
    }
  }
}

function resetProductInitials() {
  productState.checked = false;
  productState.letters = '';
  productState.consent = false;
  for (const wrapper of document.querySelectorAll('[data-mt-initials]')) {
    const toggle = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-toggle]'));
    const input = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-input]'));
    if (toggle) toggle.checked = false;
    if (input) input.value = '';
    const consent = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-consent]'));
    if (consent) consent.checked = false;
    applyToggleState(wrapper);
  }
}

/**
 * Aggiunge il prodotto "Iniziali personalizzate" collegato a un capo.
 * @param {{ quantity: number, letters: string, garment: string }} options
 */
async function addInitialsLine({ quantity, letters, garment }) {
  return postJson(CART_ADD_URL, {
    items: [
      {
        id: Number(cfg.initialsVariantId),
        quantity: Math.max(1, quantity || 1),
        properties: { [INITIALS_PROPERTY]: letters, [GARMENT_PROPERTY]: garment, [CONSENT_PROPERTY]: CONSENT_VALUE },
      },
    ],
    sections: cartSectionIds().join(','),
  });
}

/* -------------------------------------------------------------------------- */
/* Carrello                                                                    */
/* -------------------------------------------------------------------------- */

/** @param {any} cart */
function buildCartBlock(cart) {
  const realItems = cart.items.filter((/** @type {any} */ item) => !isAddon(item));
  if (!realItems.length) return null;

  const candidates = realItems.filter(
    (/** @type {any} */ item) => !item.gift_card && !(item.properties && item.properties[INITIALS_PROPERTY])
  );
  const boxLine = cart.items.find((/** @type {any} */ item) => item.handle === BOX_HANDLE);
  const uid = ++instanceCounter;
  const parts = [];

  if (cfg.initialsVariantId && candidates.length) {
    const garmentControl =
      candidates.length > 1
        ? `<select class="mt-cart__select" data-mt-cart-initials-line aria-label="${escapeHtml(TEXT.cartInitialsGarment)}">
            ${candidates
              .map(
                (/** @type {any} */ item) =>
                  `<option value="${escapeHtml(item.key)}">${escapeHtml(item.product_title)}${
                    item.variant_title ? ` – ${escapeHtml(item.variant_title)}` : ''
                  }</option>`
              )
              .join('')}
          </select>`
        : `<input type="hidden" value="${escapeHtml(candidates[0].key)}" data-mt-cart-initials-line>
           <span class="mt-cart__garment">${escapeHtml(candidates[0].product_title)}</span>`;

    parts.push(`
      <div class="mt-cart__block" data-mt-cart-initials>
        <p class="mt-cart__title">${escapeHtml(TEXT.cartInitialsTitle)} <span class="mt-cart__price">${escapeHtml(
          initialsPriceLabel()
        )}</span></p>
        <p class="mt-cart__text">${escapeHtml(TEXT.cartInitialsText)}</p>
        ${garmentControl}
        <div class="mt-cart__row">
          <input
            class="mt-cart__input"
            type="text"
            maxlength="2"
            placeholder="MM"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            aria-label="${escapeHtml(TEXT.productFieldLabel)}"
            data-mt-cart-initials-input
          >
          <button type="button" class="button button-secondary mt-cart__button" data-mt-cart-initials-add>
            ${escapeHtml(TEXT.cartInitialsButton)}
          </button>
        </div>
        ${consentMarkup('data-mt-cart-initials-consent')}
        <small class="mt-cart__error" data-mt-cart-error hidden></small>
      </div>
    `);
  }

  if (cfg.boxVariantId) {
    const name = `mt-packaging-${uid}`;
    parts.push(`
      <fieldset class="mt-cart__block mt-cart__packaging">
        <legend class="mt-cart__title">${escapeHtml(TEXT.packagingTitle)}</legend>
        <label class="mt-cart__option">
          <input type="radio" name="${name}" value="bag" data-mt-packaging ${boxLine ? '' : 'checked'}>
          <span class="mt-cart__option-label">${escapeHtml(TEXT.packagingBag)}</span>
          <span class="mt-cart__option-price">${escapeHtml(TEXT.free)}</span>
        </label>
        <label class="mt-cart__option">
          <input type="radio" name="${name}" value="box" data-mt-packaging ${boxLine ? 'checked' : ''}>
          <span class="mt-cart__option-label">${escapeHtml(TEXT.packagingBox)}</span>
          <span class="mt-cart__option-price">+${escapeHtml(cfg.boxPrice || '')}</span>
        </label>
        <small class="mt-cart__error" data-mt-cart-error hidden></small>
      </fieldset>
    `);
  }

  if (!parts.length) return null;

  const wrapper = document.createElement('div');
  wrapper.className = 'mt-cart';
  wrapper.dataset.mtCart = '';
  wrapper.innerHTML = parts.join('');
  return wrapper;
}

/** @param {boolean} [force] */
function renderCartBlocks(force = false) {
  if (!lastCart) return;
  const anchors = document.querySelectorAll('.cart-totals');

  for (const anchor of anchors) {
    const existing = anchor.previousElementSibling;
    const hasBlock = existing instanceof HTMLElement && existing.dataset.mtCart !== undefined;
    if (hasBlock && !force) continue;
    if (hasBlock) existing.remove();

    const block = buildCartBlock(lastCart);
    if (block) anchor.before(block);
  }
}

/** @param {Element} scope @param {string} message */
function showCartError(scope, message) {
  const error = /** @type {HTMLElement | null} */ (scope.querySelector('[data-mt-cart-error]'));
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

/** @param {HTMLElement} block @param {boolean} state */
function setBusy(block, state) {
  busy = state;
  block.classList.toggle('mt-cart--busy', state);
  for (const control of block.querySelectorAll('input, select, button')) {
    /** @type {HTMLInputElement} */ (control).disabled = state;
  }
}

/** @param {HTMLElement} button */
async function handleCartInitials(button) {
  const block = /** @type {HTMLElement | null} */ (button.closest('[data-mt-cart-initials]'));
  const wrapper = /** @type {HTMLElement | null} */ (button.closest('[data-mt-cart]'));
  if (!block || !wrapper || busy) return;

  const input = /** @type {HTMLInputElement | null} */ (block.querySelector('[data-mt-cart-initials-input]'));
  const lineControl = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
    block.querySelector('[data-mt-cart-initials-line]')
  );
  const letters = sanitizeLetters(input?.value || '');
  if (letters.length !== 2) {
    showCartError(block, TEXT.invalidLetters);
    input?.focus();
    return;
  }
  const consent = /** @type {HTMLInputElement | null} */ (block.querySelector('[data-mt-cart-initials-consent]'));
  if (!consent?.checked) {
    showCartError(block, TEXT.consentRequired);
    consent?.focus();
    return;
  }
  showCartError(block, '');

  const cart = lastCart || (await fetchCart());
  const item = cart.items.find((/** @type {any} */ line) => line.key === lineControl?.value);
  if (!item) return;

  setBusy(wrapper, true);
  try {
    await postJson(CART_CHANGE_URL, {
      id: item.key,
      quantity: item.quantity,
      properties: { ...(item.properties || {}), [INITIALS_PROPERTY]: letters, [CONSENT_PROPERTY]: CONSENT_VALUE },
    });
    const response = await addInitialsLine({ quantity: item.quantity, letters, garment: item.product_title });
    await refreshCartUI(response.sections);
  } catch (error) {
    console.warn('[metielle-addons]', error);
    setBusy(wrapper, false);
    showCartError(block, TEXT.genericError);
  }
}

/** @param {HTMLInputElement} radio */
async function handlePackaging(radio) {
  const wrapper = /** @type {HTMLElement | null} */ (radio.closest('[data-mt-cart]'));
  const fieldset = radio.closest('fieldset');
  if (!wrapper || !fieldset || busy) return;

  const cart = await fetchCart();
  const boxLine = cart.items.find((/** @type {any} */ item) => item.handle === BOX_HANDLE);
  const wantsBox = radio.value === 'box';
  if (wantsBox === Boolean(boxLine)) return;

  setBusy(wrapper, true);
  try {
    const sections = cartSectionIds().join(',');
    const response = wantsBox
      ? await postJson(CART_ADD_URL, { items: [{ id: Number(cfg.boxVariantId), quantity: 1 }], sections })
      : await postJson(CART_CHANGE_URL, { id: boxLine.key, quantity: 0, sections });
    await refreshCartUI(response.sections);
  } catch (error) {
    console.warn('[metielle-addons]', error);
    setBusy(wrapper, false);
    showCartError(fieldset, TEXT.genericError);
  }
}

/**
 * Rimuove iniziali senza capo collegato, scatole senza capi e quantità in eccesso.
 * @param {any} cart
 */
async function cleanupOrphans(cart) {
  if (!cart?.items?.length) return false;
  const realItems = cart.items.filter((/** @type {any} */ item) => !isAddon(item));
  /** @type {Record<string, number>} */
  const updates = {};

  for (const item of cart.items) {
    if (item.handle === BOX_HANDLE) {
      if (!realItems.length) updates[item.key] = 0;
      else if (item.quantity > 1) updates[item.key] = 1;
    }

    if (item.handle === INITIALS_HANDLE) {
      const letters = item.properties?.[INITIALS_PROPERTY];
      const garment = item.properties?.[GARMENT_PROPERTY];
      const matchingQuantity = realItems
        .filter(
          (/** @type {any} */ line) =>
            line.properties?.[INITIALS_PROPERTY] === letters && line.product_title === garment
        )
        .reduce((/** @type {number} */ sum, /** @type {any} */ line) => sum + line.quantity, 0);
      if (item.quantity > matchingQuantity) updates[item.key] = matchingQuantity;
    }
  }

  if (!Object.keys(updates).length) return false;

  const response = await postJson(CART_UPDATE_URL, { updates, sections: cartSectionIds().join(',') });
  await refreshCartUI(response.sections);
  return true;
}

let cleanupTimer = 0;
function scheduleCartSync() {
  window.clearTimeout(cleanupTimer);
  cleanupTimer = window.setTimeout(async () => {
    try {
      const cart = await fetchCart();
      const cleaned = await cleanupOrphans(cart);
      if (!cleaned) renderCartBlocks(true);
    } catch (error) {
      console.warn('[metielle-addons]', error);
    }
  }, 250);
}

/* -------------------------------------------------------------------------- */
/* Eventi                                                                      */
/* -------------------------------------------------------------------------- */

document.addEventListener('change', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  if (target.matches?.('[data-mt-initials-toggle]')) {
    const wrapper = target.closest('[data-mt-initials]');
    productState.checked = /** @type {HTMLInputElement} */ (target).checked;
    if (wrapper) {
      applyToggleState(wrapper);
      if (productState.checked) {
        /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-mt-initials-input]'))?.focus();
      }
    }
  }

  if (target.matches?.('[data-mt-initials-consent]')) {
    productState.consent = /** @type {HTMLInputElement} */ (target).checked;
    validateConsent(/** @type {HTMLInputElement} */ (target));
  }

  if (target.matches?.('[data-mt-packaging]')) {
    handlePackaging(/** @type {HTMLInputElement} */ (target));
  }
});

document.addEventListener('input', (event) => {
  const target = /** @type {HTMLInputElement} */ (event.target);
  if (target.matches?.('[data-mt-initials-input], [data-mt-cart-initials-input]')) {
    const clean = sanitizeLetters(target.value);
    if (clean !== target.value) target.value = clean;
    if (target.matches('[data-mt-initials-input]')) {
      productState.letters = clean;
      validateProductInput(target);
    }
  }
});

document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement | null} */ (
    /** @type {HTMLElement} */ (event.target).closest?.('[data-mt-cart-initials-add]')
  );
  if (button) {
    event.preventDefault();
    handleCartInitials(button);
  }
});

document.addEventListener('keydown', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (event.key === 'Enter' && target.matches?.('[data-mt-cart-initials-input]')) {
    event.preventDefault();
    const button = target.closest('[data-mt-cart-initials]')?.querySelector('[data-mt-cart-initials-add]');
    if (button) handleCartInitials(/** @type {HTMLElement} */ (button));
  }
});

// Aggiunta al carrello dalla pagina prodotto: se sono state scelte le iniziali, aggiunge il servizio.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const anyEvent = /** @type {any} */ (event);
  if (anyEvent.__metielle) return;

  const productForm = /** @type {HTMLElement | null} */ (
    /** @type {HTMLElement} */ (event.target)?.closest?.('product-form-component')
  );
  const form = productForm?.querySelector('form');
  const toggle = /** @type {HTMLInputElement | null} */ (form?.querySelector('[data-mt-initials-toggle]') ?? null);
  const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('[data-mt-initials-input]') ?? null);
  const consent = /** @type {HTMLInputElement | null} */ (form?.querySelector('[data-mt-initials-consent]') ?? null);

  const wantsInitials =
    productInitialsEnabled() &&
    form &&
    toggle?.checked &&
    consent?.checked &&
    input &&
    /^[A-Z]{2}$/.test(input.value);
  const letters = input?.value || '';
  const quantity = form ? Number(new FormData(form).get('quantity')) || 1 : 1;

  anyEvent.promise
    ?.then(async (/** @type {any} */ result) => {
      const detail = result?.detail;
      if (wantsInitials && !detail?.didError) {
        try {
          const response = await addInitialsLine({ quantity, letters, garment: cfg.productTitle });
          resetProductInitials();
          await refreshCartUI(response.sections);
          return;
        } catch (error) {
          console.warn('[metielle-addons] Iniziali non aggiunte', error);
        }
      }
      scheduleCartSync();
    })
    .catch(() => scheduleCartSync());
});

/* -------------------------------------------------------------------------- */
/* Avvio                                                                       */
/* -------------------------------------------------------------------------- */

let mutationTimer = 0;
const observer = new MutationObserver(() => {
  window.clearTimeout(mutationTimer);
  mutationTimer = window.setTimeout(() => {
    injectProductInitials();
    renderCartBlocks(false);
  }, 50);
});

function start() {
  injectProductInitials();
  observer.observe(document.body, { childList: true, subtree: true });
  scheduleCartSync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
